import type { KeyboardEventHandler, MouseEventHandler } from 'react';
import { useCallback } from 'react';

import type { EPackageTiers } from '@feathr/blackbox';

import type { ITrackBlockedNavFunctionProps } from './useBlockedNavAnalytics';
import {
  type IAnalyticsContext,
  type IUseFeatureAccessAnalyticsOptions,
  useBlockedNavAnalytics,
} from './useBlockedNavAnalytics';

interface IComposableProps {
  onClick?: MouseEventHandler;
  onKeyDown?: KeyboardEventHandler;
  onMouseEnter?: () => void;
  'aria-disabled'?: boolean;
  tabIndex?: number;
  [key: string]: unknown;
}

export interface IUseTrackableOptions {
  featureName?: string;
  requiredPackage?: EPackageTiers;
  disabled?: boolean;
  contextData?: Record<string, unknown>;
  analyticsContext?: IAnalyticsContext;
  analyticsOptions?: IUseFeatureAccessAnalyticsOptions;
}

/**
 * Hook for adding analytics tracking to interactive elements when they are disabled/locked
 * Uses a compose handlers pattern to properly merge with existing event handlers
 */
export function useTrackable({
  featureName,
  requiredPackage,
  disabled = false,
  contextData,
  analyticsContext = {},
  analyticsOptions = {},
}: IUseTrackableOptions): {
  composeHandlers: (existingProps: IComposableProps) => IComposableProps;
  trackBlockedNavClick: ITrackBlockedNavFunctionProps;
  trackBlockedNavHover: ITrackBlockedNavFunctionProps;
} {
  const { trackBlockedNavClick, trackBlockedNavHover } = useBlockedNavAnalytics(
    analyticsContext,
    analyticsOptions,
  );

  const createComposedClickHandler = useCallback(
    (originalHandler?: (event: React.MouseEvent) => void) => {
      return (event: React.MouseEvent) => {
        if (disabled && featureName) {
          event.preventDefault();
          event.stopPropagation();
          trackBlockedNavClick(featureName, requiredPackage, contextData);
          return;
        }

        originalHandler?.(event);
      };
    },
    [disabled, featureName, requiredPackage, contextData, trackBlockedNavClick],
  );

  const createComposedKeyHandler = useCallback(
    (originalHandler?: (event: React.KeyboardEvent) => void) => {
      return (event: React.KeyboardEvent) => {
        if (disabled && featureName && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          event.stopPropagation();
          trackBlockedNavClick(featureName, requiredPackage, contextData);
          return;
        }

        originalHandler?.(event);
      };
    },
    [disabled, featureName, requiredPackage, contextData, trackBlockedNavClick],
  );

  const createComposedHoverHandler = useCallback(
    (originalHandler?: () => void) => {
      return () => {
        if (disabled && featureName) {
          trackBlockedNavHover(featureName, requiredPackage, contextData);
        }

        originalHandler?.();
      };
    },
    [disabled, featureName, requiredPackage, contextData, trackBlockedNavHover],
  );

  return {
    composeHandlers: useCallback(
      (existingProps: IComposableProps = {}) => ({
        ...existingProps,
        onClick: createComposedClickHandler(existingProps.onClick),
        onKeyDown: createComposedKeyHandler(existingProps.onKeyDown),
        onMouseEnter: createComposedHoverHandler(existingProps.onMouseEnter),
        'aria-disabled': disabled || existingProps['aria-disabled'],
        tabIndex: disabled ? 0 : existingProps.tabIndex,
      }),
      [createComposedClickHandler, createComposedKeyHandler, createComposedHoverHandler, disabled],
    ),
    trackBlockedNavClick,
    trackBlockedNavHover,
  };
}

export default useTrackable;
